// Shared fetch wrapper for server API calls.
// Sends credentials (HttpOnly cookies), parses JSON, maps errors to ApiError.

import { ApiError } from './apiError'
import type { ApiErrorCode } from './apiError'
import { getServerBaseUrl } from './baseUrl'
import { isWatching } from './watchLock'

/**
 * The methods that only read.
 *
 * **Watch mode refuses everything else** (`todo-takeout.md` entry 6.25). The
 * hidden navigation is the lock's *consequence*; this is the lock. A route
 * reached by a typed URL, a restored tab or a keyboard shortcut still cannot
 * write, because every write in this app leaves through this function.
 */
const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

function mapStatus(status: number): ApiErrorCode {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 400 || status === 422) return 'validation_error'
  if (status === 409) return 'conflict'
  // The server's Premium gate (billing-lifecycle.md §4.4); the portal maps it to
  // the upgrade card rather than an error wall.
  if (status === 402) return 'plan_required'
  if (status >= 500) return 'server_error'
  return 'unknown'
}

/** Whether this request carries a body at all. An empty string is not one. */
function hasBody(init?: RequestInit): boolean {
  return init?.body !== undefined && init.body !== null && init.body !== ''
}

export async function serverFetch<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  // Before the request is built, so nothing is sent and no signature is asked
  // for. The lock is read from storage on every call rather than captured
  // once: a tab locked in another tab is locked here too.
  const method = (init?.method ?? 'GET').toUpperCase()
  if (!READ_METHODS.has(method) && isWatching()) {
    throw new ApiError('forbidden', 'This screen is in watch mode. Unlock it to make changes.')
  }

  const baseUrl = getServerBaseUrl()
  const url = `${baseUrl}${path}`

  let res: Response
  try {
    res = await fetch(url, {
      ...init,
      credentials: 'include',
      headers: {
        // Only when something is actually being sent. Fastify parses a body
        // whenever this header claims JSON, and rejects an empty one with
        // "Body cannot be empty when content-type is set to 'application/json'".
        // Declaring it unconditionally made every bodyless DELETE fail — the
        // one that reached a user was deleting a bookable event.
        ...(hasBody(init) ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    })
  } catch (err) {
    throw new ApiError(
      'network_error',
      `Network error: ${err instanceof Error ? err.message : 'fetch failed'}`
    )
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`
    let details: Record<string, unknown> | undefined
    try {
      const body = await res.json()
      message = body?.error?.message ?? body?.message ?? message
      // Carried through so a caller can act on a refusal that names what to
      // fix — the dates a stock write would drop below reserved, say.
      if (body?.error && typeof body.error === 'object') details = body.error
      // A refusal that lists what it would change beside `error`, like the draft
      // publish's `identity_change` answer, would lose that list here. It is carried
      // under the same name as the stock refusal's, which lives inside `error`.
      if (details && !('conflicts' in details) && Array.isArray(body?.conflicts)) {
        details = { ...details, conflicts: body.conflicts }
      }
    } catch {
      // non-JSON error body — use status text
    }
    throw new ApiError(mapStatus(res.status), message, res.status, details)
  }

  return res.json() as Promise<T>
}
